/**
 * <cart-form> — Web Component that progressively enhances the cart page's
 * form. Without JS, the form submits natively to /cart (POST) and Shopify
 * processes quantity updates, note, and checkout redirect. With JS, this
 * component intercepts interactions and uses the Cart API for inline updates.
 *
 * Markup contract:
 *   <cart-form>
 *     <form data-cart-form action="/cart" method="post">
 *       <div data-cart-filled>
 *         <div data-cart-items>
 *           <div data-cart-item data-line-item-key="...">
 *             <span data-line-price>...</span>
 *             <div data-quantity-stepper>
 *               <button data-quantity-decrement>−</button>
 *               <input data-quantity-input type="number" ... />
 *               <button data-quantity-increment>+</button>
 *             </div>
 *             <button data-remove-item>...</button>
 *           </div>
 *         </div>
 *       </div>
 *       <textarea data-cart-note>...</textarea>
 *       <span data-cart-subtotal>...</span>
 *       <div data-cart-status role="status" aria-live="polite"></div>
 *     </form>
 *     <template data-empty-template>...</template>
 *   </cart-form>
 */

class CartForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-cart-form]');
    this.filledState = this.querySelector('[data-cart-filled]');
    this.itemsContainer = this.querySelector('[data-cart-items]');
    this.subtotalEl = this.querySelector('[data-cart-subtotal]');
    this.statusEl = this.querySelector('[data-cart-status]');
    this.noteField = this.querySelector('[data-cart-note]');
    this.emptyTemplate = this.querySelector('[data-empty-template]');

    if (!this.form) return;

    this.lastSubmitter = null;
    this.form.addEventListener('click', this.captureSubmitter, true);
    this.form.addEventListener('click', this.onClick);
    this.form.addEventListener('submit', this.onSubmit);

    if (this.itemsContainer) {
      this.itemsContainer.addEventListener('input', this.onQuantityInput);
    }

    if (this.noteField) {
      this.noteField.addEventListener('change', this.onNoteChange);
    }

    this.activeRequests = new Map();
    this.quantityDebounceTimers = new Map();
    this.noteDebounceTimer = null;
  }

  disconnectedCallback() {
    if (!this.form) return;
    this.form.removeEventListener('click', this.captureSubmitter, true);
    this.form.removeEventListener('click', this.onClick);
    this.form.removeEventListener('submit', this.onSubmit);
    if (this.itemsContainer) {
      this.itemsContainer.removeEventListener('input', this.onQuantityInput);
    }
    if (this.noteField) {
      this.noteField.removeEventListener('change', this.onNoteChange);
    }
    for (const controller of this.activeRequests.values()) {
      controller.abort();
    }
    for (const timer of this.quantityDebounceTimers.values()) {
      clearTimeout(timer);
    }
    clearTimeout(this.noteDebounceTimer);
  }

  captureSubmitter = (event) => {
    if (event.target.matches('button[type="submit"]')) {
      this.lastSubmitter = event.target;
    }
  };

  onSubmit = (event) => {
    const submitter = event.submitter || this.lastSubmitter;
    this.lastSubmitter = null;

    if (submitter && submitter.name === 'update') return;

    event.preventDefault();

    if (submitter && submitter.name === 'checkout') {
      if (this.noteField) {
        this.saveNote(this.noteField.value);
      }
      window.location.href = '/checkout';
    }
  };

  onClick = (event) => {
    const target = event.target;

    const increment = target.closest('[data-quantity-increment]');
    if (increment) {
      const item = increment.closest('[data-cart-item]');
      if (item) this.stepQuantity(item, 1);
      return;
    }

    const decrement = target.closest('[data-quantity-decrement]');
    if (decrement) {
      const item = decrement.closest('[data-cart-item]');
      if (item) this.stepQuantity(item, -1);
      return;
    }

    const remove = target.closest('[data-remove-item]');
    if (remove) {
      const item = remove.closest('[data-cart-item]');
      if (item) this.removeItem(item);
      return;
    }
  };

  onQuantityInput = (event) => {
    const input = event.target;
    if (!input.matches('[data-quantity-input]')) return;

    const item = input.closest('[data-cart-item]');
    if (!item) return;
    const key = item.dataset.lineItemKey;

    clearTimeout(this.quantityDebounceTimers.get(key));
    this.quantityDebounceTimers.set(
      key,
      setTimeout(() => {
        const qty = Math.max(1, Number(input.value) || 1);
        input.value = String(qty);
        this.updateQuantity(key, qty, item);
      }, 300),
    );
  };

  onNoteChange = () => {
    clearTimeout(this.noteDebounceTimer);
    this.noteDebounceTimer = setTimeout(() => {
      this.saveNote(this.noteField.value);
    }, 500);
  };

  stepQuantity(itemEl, delta) {
    const input = itemEl.querySelector('[data-quantity-input]');
    if (!input) return;
    const current = Number(input.value) || 1;
    const min = Number(input.min) || 1;
    const next = Math.max(min, current + delta);
    if (next === current) return;
    input.value = String(next);
    const key = itemEl.dataset.lineItemKey;
    clearTimeout(this.quantityDebounceTimers.get(key));
    this.updateQuantity(key, next, itemEl);
  }

  async removeItem(itemEl) {
    const key = itemEl.dataset.lineItemKey;
    const title = itemEl.querySelector('[data-item-title]')?.textContent?.trim() || '';

    const nextSibling = itemEl.nextElementSibling;

    itemEl.classList.add('cart-line-item--removing');

    await new Promise((resolve) => {
      itemEl.addEventListener('transitionend', resolve, { once: true });
      setTimeout(resolve, 250);
    });

    await this.updateQuantity(key, 0, itemEl);

    itemEl.remove();

    const remaining = this.itemsContainer?.querySelectorAll('[data-cart-item]');
    if (!remaining || remaining.length === 0) {
      this.swapToEmptyState();
    } else if (nextSibling && nextSibling.matches('[data-cart-item]')) {
      const focusTarget = nextSibling.querySelector('[data-item-title]');
      if (focusTarget) focusTarget.focus();
    } else if (remaining.length > 0) {
      const lastItem = remaining[remaining.length - 1];
      const focusTarget = lastItem.querySelector('[data-item-title]');
      if (focusTarget) focusTarget.focus();
    }

    if (title) {
      this.announce(
        this.dataset.itemRemovedMessage?.replace('__TITLE__', title) ||
          `${title} removed from cart`,
      );
    }
  }

  async updateQuantity(key, quantity, itemEl) {
    if (this.activeRequests.has(key)) {
      this.activeRequests.get(key).abort();
    }

    const controller = new AbortController();
    this.activeRequests.set(key, controller);

    if (itemEl && quantity > 0) {
      this.setLoading(itemEl, true);
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id: key, quantity }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        const message = body?.description || body?.message || this.errorMessage();
        this.announce(message);
        const input = itemEl?.querySelector('[data-quantity-input]');
        if (input) {
          await this.refreshCart();
        }
        return;
      }

      const cart = await response.json();
      this.patchDom(cart);

      if (quantity > 0) {
        this.announce(this.dataset.updatedMessage || 'Cart updated');
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.announce(this.errorMessage());
    } finally {
      this.activeRequests.delete(key);
      if (itemEl && quantity > 0) {
        this.setLoading(itemEl, false);
      }
    }
  }

  async saveNote(note) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ note }),
      });
    } catch {
      this.announce(this.errorMessage());
    }
  }

  async refreshCart() {
    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (response.ok) {
        const cart = await response.json();
        this.patchDom(cart);
      }
    } catch {
      // Silent — the page still shows the last known state.
    }
  }

  patchDom(cart) {
    if (this.subtotalEl) {
      this.subtotalEl.textContent = this.formatMoney(cart.total_price);
    }

    for (const cartItem of cart.items) {
      const el = this.querySelector(`[data-line-item-key="${CSS.escape(cartItem.key)}"]`);
      if (!el) continue;

      const priceEl = el.querySelector('[data-line-price]');
      if (priceEl) {
        priceEl.textContent = this.formatMoney(cartItem.original_line_price);
      }

      const qtyInput = el.querySelector('[data-quantity-input]');
      if (qtyInput && Number(qtyInput.value) !== cartItem.quantity) {
        qtyInput.value = String(cartItem.quantity);
      }
    }

    this.updateHeaderBadge(cart.item_count);
  }

  swapToEmptyState() {
    if (this.emptyTemplate && this.form) {
      const emptyContent = this.emptyTemplate.content.cloneNode(true);
      this.form.replaceWith(emptyContent);
      this.emptyTemplate.remove();

      const continueLink = this.parentElement?.querySelector('[data-cart-empty] a');
      if (continueLink) continueLink.focus();
    }

    this.updateHeaderBadge(0);
  }

  updateHeaderBadge(count) {
    const badge = document.querySelector('[data-cart-count]');
    if (badge) {
      badge.dataset.cartCount = String(count);
    }

    const indicator = document.querySelector('[data-cart-count] svg circle[fill="#F15C20"]');
    if (indicator) {
      indicator.style.display = count > 0 ? '' : 'none';
    }
  }

  setLoading(itemEl, isLoading) {
    if (isLoading) {
      itemEl.setAttribute('aria-busy', 'true');
    } else {
      itemEl.removeAttribute('aria-busy');
    }
  }

  announce(message) {
    if (!this.statusEl) return;
    this.statusEl.textContent = '';
    requestAnimationFrame(() => {
      this.statusEl.textContent = message;
    });
  }

  errorMessage() {
    return this.dataset.errorMessage || 'Could not update cart. Please try again.';
  }

  formatMoney(cents) {
    // Match the Liquid `money-clean` snippet: drop the trailing ".00" on
    // whole-dollar amounts, keep 2 decimals when there are cents.
    const hasCents = cents % 100 !== 0;
    return (
      '$' +
      (cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2,
      })
    );
  }
}

if (!customElements.get('cart-form')) {
  customElements.define('cart-form', CartForm);
}
